import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ImgLoader } from "@treegram/loaders-img";
import { ViewerLoader } from "@/hooks/viewer/use-viewer.ts";

import useProjectFilePlanContext from "../context/use-project-file-plan-context.ts";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import PlanFormat from "@/pages/takeoff/context/project-organisation/types/plan-format.ts";
import PlanScale from "@/pages/takeoff/context/project-organisation/types/plan-scale.ts";
import PlanOrientation from "@/pages/takeoff/context/project-organisation/types/plan-orientation.ts";

export const useOrganisationPlanLoaders = (): ViewerLoader[] => {
    const { dataContent, urlFilePlan, storeyFile } = useProjectFilePlanContext();

    // Workaround to prevent loading unnecessarily (planPdfLoader)
    const [mode, setMode] = useState<"KnownScaleContent" | "UserDefinedContent">(
        dataContent instanceof UserDefinedContent ?
            "UserDefinedContent" : "KnownScaleContent");
    const knownFormatRef = useRef<PlanFormat>("A3");
    const knownScaleRef = useRef<PlanScale>("1:100");
    const knownOrientationRef = useRef<PlanOrientation>("LANDSCAPE");
    const definedscaleRef = useRef<number>(1);

    useEffect(() => {
        if (dataContent instanceof KnownScaleContent) {
            knownFormatRef.current = dataContent.format;
            knownScaleRef.current = dataContent.scale;
            knownOrientationRef.current = dataContent.orientation;
            setMode("KnownScaleContent");
        }
        else if (dataContent instanceof UserDefinedContent) {
            definedscaleRef.current = dataContent.scale ?? 1;
            setMode("UserDefinedContent");
        }
    }, [dataContent]);

    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, typeof storeyFile>>(() => ({
        key: "plan-pdf-loader",
        factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
        itemsToLoad: storeyFile != null ? [storeyFile] : undefined,
        fetchUrl: () => urlFilePlan,
        name: (item) => item?.name ?? "plan",
        getOptions: () => {
            // Use workaround with references defined previously
            let loaderOptions = {};
            if (mode === "KnownScaleContent") {
                loaderOptions = { format: knownFormatRef.current, scale: knownScaleRef.current, orientation: knownOrientationRef.current };
            } else if (mode === "UserDefinedContent") {
                loaderOptions = { definedScale: definedscaleRef.current };
            }
            return { useCoordinates: false, loaderOptions };
        },
        onItemLoaded: async (ctx, loader) => {
            const dimensionsCanvas = loader.getDimensionsCanvas();
            if (dimensionsCanvas) {
                await ctx.viewer?.getCamera().controls.setLookAt(0, Math.max(dimensionsCanvas.height, dimensionsCanvas.width) * 0.9, 0, 0, 0, 0);
            }
        },
        onError: (_ctx, _loader, _item, error) => {
            toast.error(error.message, { autoClose: false });
        },
        enabled: urlFilePlan != null && storeyFile != null,
    }), [storeyFile, urlFilePlan, mode]);

    return useMemo<ViewerLoader[]>(() => [planPdfLoader], [planPdfLoader]);
};

export default useOrganisationPlanLoaders;
